import React, { useEffect, Fragment, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import {
  BulkImportBillingChargesGroup,
  StoreBillingChargesGroup
} from "../../../api/masters/billingChargesGroupAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  creditAcc: yup.string().required("Credit Acc is required"),
  printSrNo: yup.string().required("Print Sr No is required"),
  fuel: yup.string().required("Fuel is required"),
  code: yup.string().optional(),
  hsnCode: yup.string().optional()
});

const AddBillingChargesGroup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fuel: "No"
    }
  });

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await StoreBillingChargesGroup(data);

      if (response.success) {
        navigate(`/masters/billing-charges-group/list`);
        showToast("success", response.message);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "name", label: "Name", sample: "Name" },
    { key: "creditAcc", label: "creditAcc", sample: "creditAcc" },
    { key: "printSrNo", label: "printSrNo", sample: "printSrNo" },
    { key: "fuel", label: "fuel", sample: "fuel" },
    { key: "code", label: "code", sample: "code" },
    { key: "hsnCode", label: "hsnCode", sample: "hsnCode" }
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportBillingChargesGroup(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/billing-charges-group/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb
        parent="Billing Charges Group"
        title="Add Billing Charges Group"
      />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <h5>Add Billing Charges Group</h5>
                  <Link to={`/masters/billing-charges-group/list`}>
                    <button className="btn btn-primary" id="list">
                      List Billing Charges Group
                    </button>
                    <UncontrolledTooltip placement="top" target="list">
                      List Billing Charges Group
                    </UncontrolledTooltip>
                  </Link>
                </div>
              </div>
              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          className="form-control"
                          id="name"
                          type="text"
                          placeholder="Billing Charges Group Name"
                          {...register("name")}
                        />
                        {errors.name &&
                          <p className="text-danger">
                            {errors.name.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="creditAcc">Credit Account *</label>
                        <input
                          className="form-control"
                          id="creditAcc"
                          type="text"
                          placeholder="Credit Account"
                          {...register("creditAcc")}
                        />
                        {errors.creditAcc &&
                          <p className="text-danger">
                            {errors.creditAcc.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="printSrNo">Print Sr No *</label>
                        <input
                          className="form-control"
                          id="printSrNo"
                          type="number"
                          placeholder="Print Sr No"
                          {...register("printSrNo")}
                        />
                        {errors.printSrNo &&
                          <p className="text-danger">
                            {errors.printSrNo.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="fuel">Fuel</label>
                        <select className="form-control" {...register("fuel")}>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                        {errors.fuel &&
                          <p className="text-danger">
                            {errors.fuel.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="code">Code</label>
                        <input
                          className="form-control"
                          id="code"
                          type="text"
                          placeholder="Code"
                          {...register("code")}
                        />
                        {errors.code &&
                          <p className="text-danger">
                            {errors.code.message}
                          </p>}
                      </div>
                    </div>

                    <div className="col-12">
                      <div className="form-group">
                        <label htmlFor="hsnCode">HSN Code</label>
                        <input
                          className="form-control"
                          id="hsnCode"
                          type="text"
                          placeholder="HSN Code"
                          {...register("hsnCode")}
                        />
                        {errors.hsnCode &&
                          <p className="text-danger">
                            {errors.hsnCode.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="billingCharges"
              sampleFileName="billingCharge_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddBillingChargesGroup;
